// ---------------------------------------------------------------------------
// The site's stock claims indexed by material — the one place that decides
// which of the OTHER tasks' claims count against a task's own.
//
// Two different questions, answered from one index (ruling 10, 2026-09-09):
//   - Taken elements: an element id is global, so a claim on ANY floor holding
//     it takes it away — every other claim for the material counts.
//   - Claimed quantity ("Claimed by …", "already claimed by other tasks"):
//     only claims whose floors overlap the task's, where a claim (or a task)
//     with no floors is scoped to the whole model and overlaps everything.
//
// Shared by `useTaskStock` (one task) and `useStockFlags` (every task on the
// site). Pure functions — no React, no store; unit-tested in isolation.
// ---------------------------------------------------------------------------

import type { IClaimElements, ISchedulerTask, ITaskStockClaim } from '@interfaces/IScheduler';
import { fold } from '../../LogisticsPage/units';

/**
 * The task's OWN claim for `materialName` other than the row `exceptId`, or
 * null. One claim per material per task (ruling 95, contract v13): a pick
 * naming a material the task already claims grows that claim rather than
 * starting a second row. A blank name matches nothing — two rows still being
 * built are not the same material.
 */
export function ownClaimFor(claims: ITaskStockClaim[], materialName: string, exceptId: string): ITaskStockClaim | null {
  const key = fold(materialName);
  if (key === '') return null;
  return claims.find((c) => c.id !== exceptId && fold(c.materialName) === key) ?? null;
}

/** One other task's claim on a material — the breakdown the "Claimed by" box
 *  renders (the aggregate alone, `IClaimEvaluation.claimedByOthers`, is
 *  enough for the read-out but not enough to name who holds it), plus the
 *  elements it holds, which are what reduce what is left to claim. */
export interface IOtherClaim {
  taskId: string;
  taskName: string;
  quantity: number;
  /** Empty for a claim stored without elements (a free-text claim). */
  elements: IClaimElements[];
  /** The floors the claim was made on; empty means the whole model. */
  floorIds: string[];
}

/** The quantity side of another task's claim — all the ceiling arithmetic and
 *  the "already claimed by other tasks" read-out need of one. Named so the two
 *  `claimEvaluation` signatures that take it don't retype the shape. */
export type ClaimQuantityShare = Pick<IOtherClaim, 'taskId' | 'taskName' | 'quantity'>;

/** Whether two floor scopes share a floor — an empty scope is the whole
 *  model, and overlaps everything. */
export function floorsOverlap(a: string[], b: string[]): boolean {
  return a.length === 0 || b.length === 0 || a.some((id) => b.includes(id));
}

/** Every claim on the site by folded material name, in task order — built
 *  once per task list rather than re-walked per row or `evaluate()` call. */
export function indexClaimsByMaterial(tasks: ISchedulerTask[]): Map<string, IOtherClaim[]> {
  const idx = new Map<string, IOtherClaim[]>();
  for (const task of tasks) {
    for (const claim of task.stockClaims ?? []) {
      const key = fold(claim.materialName);
      const list = idx.get(key) ?? [];
      list.push({
        taskId: task.id,
        taskName: task.name,
        quantity: claim.quantity,
        elements: claim.elements ?? [],
        floorIds: claim.floorIds,
      });
      idx.set(key, list);
    }
  }
  return idx;
}

export interface IOthersForMaterial {
  /** Every other task's claim for the material, on any floor — what takes
   *  elements away (`takenElements`). */
  all: IOtherClaim[];
  /** Those whose floors overlap the task's — what the "Claimed by" box lists
   *  and the quantity read-out sums. */
  overlapping: IOtherClaim[];
}

/** The OTHER tasks' claims for `materialName`, as seen from the task
 *  `selfTaskId` scoped to `floorIds`. */
export function othersFor(
  index: Map<string, IOtherClaim[]>,
  materialName: string,
  selfTaskId: string,
  floorIds: string[],
): IOthersForMaterial {
  const all = (index.get(fold(materialName)) ?? []).filter((c) => c.taskId !== selfTaskId);
  return { all, overlapping: all.filter((c) => floorsOverlap(c.floorIds, floorIds)) };
}
